import { Button, Card, Input, Logo, useToast } from '@pharmaconnect/ui/web'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useTranslation } from '../i18n/I18nProvider'
import { supabase } from '../lib/supabase'

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)

  // Supabase auth handle automatique du recovery token via URL hash
  useEffect(() => {
    const sub = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setSessionReady(true)
      }
    })
    // Vérifier la session immédiate (déjà résolue depuis le hash)
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setSessionReady(true)
    })
    return () => {
      sub.data.subscription.unsubscribe()
    }
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError(t('reset.too_short'))
      return
    }
    if (password !== confirm) {
      setError(t('reset.mismatch'))
      return
    }
    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError
      toast.success(t('reset.success'))
      navigate('/login', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-pharmacist-bg px-4 py-8">
      <div className="mb-6">
        <Logo size={56} tone="dark" />
      </div>
      <Card className="w-full max-w-sm" noPadding>
        <div className="border-b border-white/10 p-6">
          <h1 className="text-xl font-bold text-pharmacist">{t('reset.title')}</h1>
          <p className="mt-1 text-xs text-neutral-400">{t('reset.subtitle')}</p>
        </div>

        {!sessionReady ? (
          <div className="flex flex-col gap-3 p-6" data-testid="invalid-link">
            <p className="text-sm text-neutral-300">⏳ {t('reset.checking')}</p>
            <p className="text-xs text-neutral-500">{t('reset.link_invalid')}</p>
            <Link
              to="/forgot-password"
              className="text-center text-sm text-pharmacist hover:underline"
            >
              {t('reset.request_new_link')}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6" data-testid="reset-form">
            <Input
              label={t('reset.new_password')}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="≥ 8"
              required
              data-testid="password-input"
            />
            <Input
              label={t('reset.confirm_password')}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="confirm-input"
              {...(error ? { error } : {})}
            />
            <Button type="submit" loading={loading} fullWidth disabled={!password || !confirm}>
              {t('reset.submit')}
            </Button>
          </form>
        )}
      </Card>
    </main>
  )
}
